import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useGenerateForm } from "@/features/qualifications/hooks/use-qualification-forms";
import { useQualificationFormTemplates } from "@/features/qualifications/hooks/use-qualification-templates";
import type { QualificationFormTemplate } from "@/features/qualifications/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  BriefcaseIcon,
  FileTextIcon,
  HouseIcon,
  MagicWandIcon,
  SparkleIcon,
  UsersIcon,
  type Icon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const CATEGORY_ICONS: Record<QualificationFormTemplate["category"], Icon> = {
  real_estate: HouseIcon,
  commercial: BriefcaseIcon,
  recruitment: UsersIcon,
  other: FileTextIcon,
};

interface TemplateSelectorProps {
  onSelectTemplate: (template: QualificationFormTemplate | null) => void;
  onGenerateSuccess: (generatedForm: unknown) => void;
}

/**
 * Where a new form starts: written by AI from a description, empty, or one of
 * the templates. One list of choices, the AI one first and tinted.
 */
export function TemplateSelector({
  onSelectTemplate,
  onGenerateSuccess,
}: TemplateSelectorProps) {
  const { t } = useTranslation("qualifications");
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [url, setUrl] = useState("");
  const [stepIndex, setStepIndex] = useState(0);

  const generateForm = useGenerateForm();
  const { data: templates = [], isLoading: templatesLoading } =
    useQualificationFormTemplates();

  // What the wait says, a new line every 5 seconds during generation.
  const steps = t("forms.templates.generating.steps", {
    returnObjects: true,
  }) as unknown as string[];
  const stepCount = Array.isArray(steps) ? steps.length : 1;

  useEffect(() => {
    if (generateForm.isPending) {
      const interval = setInterval(() => {
        setStepIndex((prev) => (prev + 1) % stepCount);
      }, 5000);
      return () => clearInterval(interval);
    } else {
      setStepIndex(0);
    }
  }, [generateForm.isPending, stepCount]);

  const handleGenerateFromPrompt = async () => {
    if (!prompt.trim()) {
      toast.error(t("forms.templates.messages.promptRequired"));
      return;
    }

    try {
      const generated = await generateForm.mutateAsync({
        prompt,
        url: url.trim() || undefined,
      });
      setPromptDialogOpen(false);
      toast.success(t("forms.templates.messages.generated"));
      onGenerateSuccess(generated);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("forms.templates.messages.generateError"),
      );
    }
  };

  return (
    <>
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setPromptDialogOpen(true)}
          className="flex w-full items-start gap-4 rounded-lg border border-primary/40 bg-info-soft p-4 text-left transition-colors hover:border-primary"
        >
          <div className="rounded-md bg-card p-2 text-primary-strong">
            <MagicWandIcon weight="duotone" className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="mb-1 font-medium">
              {t("forms.templates.ai.title")}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t("forms.templates.ai.description")}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onSelectTemplate(null)}
          className="flex w-full items-start gap-4 rounded-lg border border-dashed p-4 text-left transition-colors hover:border-ring/40 hover:bg-muted/40"
        >
          <div className="rounded-md bg-muted p-2 text-muted-foreground">
            <SparkleIcon weight="duotone" className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="mb-1 font-medium">
              {t("forms.templates.scratch.title")}
            </h4>
            <p className="text-sm text-muted-foreground">
              {t("forms.templates.scratch.description")}
            </p>
          </div>
        </button>

        {templatesLoading &&
          [...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        {templates.map((template, index) => {
          const CategoryIcon =
            CATEGORY_ICONS[template.category] || FileTextIcon;
          return (
            <button
              type="button"
              key={index}
              onClick={() => onSelectTemplate(template)}
              className="flex w-full items-start gap-4 rounded-lg border bg-card p-4 text-left transition-colors hover:border-ring/40 hover:bg-muted/40"
            >
              <div className="rounded-md bg-muted p-2 text-muted-foreground">
                <CategoryIcon weight="duotone" className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h4 className="font-medium">{template.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    {t(
                      `forms.templates.categories.${template.category}`,
                      template.category,
                    )}
                  </Badge>
                </div>
                <p className="mb-2 text-sm text-muted-foreground">
                  {template.description}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    {t("forms.templates.questionCount", {
                      count: template.questions.length,
                    })}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {t("forms.templates.scoring", {
                      strategy: t(
                        `forms.scoring.${template.scoring_strategy}`,
                        template.scoring_strategy,
                      ).toLowerCase(),
                    })}
                  </span>
                  {template.auto_accept_threshold && (
                    <>
                      <span aria-hidden>·</span>
                      <span>
                        {t("forms.templates.autoAccept", {
                          value: Math.round(
                            template.auto_accept_threshold * 100,
                          ),
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          {generateForm.isPending ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t("forms.templates.generating.title")}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center justify-center space-y-6 py-12">
                <Spinner />
                <p className="animate-pulse text-center text-lg font-medium">
                  {Array.isArray(steps) ? steps[stepIndex % stepCount] : ""}
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  {t("forms.templates.generating.wait")}
                </p>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t("forms.templates.dialog.title")}</DialogTitle>
                <DialogDescription>
                  {t("forms.templates.dialog.description")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="prompt">
                    {t("forms.templates.dialog.prompt")}
                  </Label>
                  <Textarea
                    id="prompt"
                    placeholder={t("forms.templates.dialog.promptPlaceholder")}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={6}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="url">
                    {t("forms.templates.dialog.url")}{" "}
                    <span className="font-normal text-muted-foreground">
                      {t("forms.templates.dialog.optional")}
                    </span>
                  </Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("forms.templates.dialog.urlHint")}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setPromptDialogOpen(false);
                    setPrompt("");
                    setUrl("");
                  }}
                >
                  {t("forms.templates.dialog.cancel")}
                </Button>
                <Button
                  onClick={handleGenerateFromPrompt}
                  disabled={!prompt.trim()}
                >
                  <MagicWandIcon className="size-4" />
                  {t("forms.templates.dialog.generate")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
